import React, { useState } from 'react';
import css from './AppointmentDetails.module.css';
import { CernerAppointmentDetails, SchedulingDetails } from '../../api/models';
import {
  H2,
  PrintLink,
  StackedColumn,
  StackedColumnSpacing,
  StackedRow,
  StackedRowSpacing,
  SuccessBanner,
} from '../../core';
import { CareProvider } from './careProvider';
import { Patient } from './patient';
import { ReasonForAppointment } from './reasonForAppointment';
import { Location } from './location';
import { DateAndTime } from './dateAndTime';
import { AppointmentReminders } from './appointmentReminders';
import { Instructions } from './instructions';
import { MyMarshfieldClinic } from './myMarshfieldClinic';
import { ResponsiveSizeUtility, isMinor } from '../../utils';
import { DateUtility, useScreenSize } from '@mchs/web-core';
import { AppointmentRemindersDialog } from './appointmentRemindersDialog';

interface AppointmentDetailsPresenterProps {
  appointmentDetails: CernerAppointmentDetails;
  schedulingDetails: SchedulingDetails;
}

const AppointmentDetailsPresenter: React.FC<
  AppointmentDetailsPresenterProps
> = (props: AppointmentDetailsPresenterProps) => {
  const [showReminderDialog, setShowReminderDialog] = useState<boolean>(true);
  const screenSize = useScreenSize();

  const narrowWidth = ResponsiveSizeUtility.isNarrowWidth(screenSize.width);

  const dateOfBirth: Date | undefined =
    DateUtility.getDateNullableFromCattailsDateString(
      props.schedulingDetails.patientDetails?.demographics?.dateOfBirth ?? null
    );
  const patientIsMinor = dateOfBirth && isMinor(dateOfBirth);

  const baseSuccessMessage =
    'Appointment successfully scheduled. Thank you for scheduling an appointment with us.';
  const successMessage =
    patientIsMinor &&
    props.schedulingDetails.contactInfoDetails?.patientContactDetails
      .emailAddress &&
    props.schedulingDetails.contactInfoDetails?.patientContactDetails
      .emailAddress.length > 0
      ? // minor with email
        `${baseSuccessMessage} A confirmation email will be sent to ${props.schedulingDetails.contactInfoDetails?.patientContactDetails.emailAddress}`
      : baseSuccessMessage;

  return (
    <>
      <StackedColumn spacing={StackedColumnSpacing.Wide}>
        <SuccessBanner text={successMessage} />
        <StackedRow>
          <div className={css.appointmentSummary}>
            <StackedColumn spacing={StackedColumnSpacing.Wide}>
              <StackedRow spacing={StackedRowSpacing.Normal}>
                <H2 text="Appointment Details" />
                <PrintLink />
              </StackedRow>
              <CareProvider
                providerName={props.appointmentDetails.providerName ?? ''}
                facilityPhoneNumber={
                  props.appointmentDetails.facilityPhoneNumber ?? ''
                }
                providerDetails={props.schedulingDetails.providerDetails}
              />
              <Patient
                patientDetails={props.schedulingDetails.patientDetails}
              />
              <ReasonForAppointment
                reasonForAppointment={
                  props.appointmentDetails.description ?? ''
                }
              />
              <Location
                facilityName={props.appointmentDetails.facilityName ?? ''}
                streetAddress={props.appointmentDetails.streetAddress ?? ''}
                city={props.appointmentDetails.city ?? ''}
                state={props.appointmentDetails.state ?? ''}
                zipCode={props.appointmentDetails.zipCode ?? ''}
              />
              <DateAndTime
                appointmentDetails={props.appointmentDetails}
                schedulingDetails={props.schedulingDetails}
              />
            </StackedColumn>
          </div>
          {!patientIsMinor && !narrowWidth ? (
            <MyMarshfieldClinic asFooter={false} />
          ) : null}
        </StackedRow>
        {!patientIsMinor ? (
          <AppointmentReminders
            contactInfoDetails={props.schedulingDetails.contactInfoDetails}
            reviewAndSubmitDetails={
              props.schedulingDetails.reviewAndSubmitDetails
            }
          />
        ) : undefined}
        <Instructions
          reminderInstructions={
            props.appointmentDetails.reminderInstructionsV2 ?? []
          }
        />
        {!patientIsMinor && narrowWidth ? (
          <MyMarshfieldClinic asFooter={true} />
        ) : null}
      </StackedColumn>
      {patientIsMinor ? (
        <AppointmentRemindersDialog
          isOpen={showReminderDialog}
          emailAddress={
            props.schedulingDetails.contactInfoDetails?.patientContactDetails
              .emailAddress
          }
          onClose={() => setShowReminderDialog(false)}
        />
      ) : null}
    </>
  );
};

export default AppointmentDetailsPresenter;
